"use client"

import clsx from "clsx"

import type { GID } from "@unlikelystudio/commerce-connector"

import type { PropsWithClassName } from "~/@types/generics"
import { Image } from "~/components/ui/Image"
import ShopTheLookPanel from "~/components/ui/Panels/ShopTheLook"
import type { ShopTheLook } from "~/components/ui/Panels/ShopTheLook/_data/serializer"
import type { TProduct } from "~/components/ui/ProductHeader/_data/types"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { usePanel } from "~/managers/PanelManager"
import { getPackshotFromImages } from "~/utils/get-packshot-from-images"

import { hideFrom, showFrom } from "~/styles/utils/show-hide"

import * as css from "./styles.css"

const MAX_THUMBNAILS = 4

type ShopTheLookCtaProps = PropsWithClassName<{
  shopTheLookItems: ShopTheLook
  shopTheLookIds: GID[]
  serializedProduct: Pick<TProduct, "handle" | "title">
  maxThumbnails?: number
  isMobile?: boolean
}>

export function ShopTheLookCta({
  shopTheLookItems,
  shopTheLookIds,
  isMobile = false,
  serializedProduct,
  className,
}: ShopTheLookCtaProps) {
  const { add } = usePanel()
  const t = useTranslate()

  const openShopTheLookPanel = () => {
    add(
      <ShopTheLookPanel
        serializedProduct={serializedProduct}
        gids={shopTheLookIds}
        items={shopTheLookItems}
        fromProductHeaderShopTheLook
      />
    )
  }

  const isMoreThanMaxThumbnails = shopTheLookItems.length > MAX_THUMBNAILS

  if (shopTheLookItems.length === 0) {
    return null
  }

  return (
    <button
      className={clsx(
        css.shopTheLookCta,
        isMobile ? hideFrom("desktop", "flex") : showFrom("desktop", "flex"),
        className
      )}
      onClick={openShopTheLookPanel}
    >
      <p className={clsx(css.shopTheLookCtaTitle, showFrom("desktop", "block"))}>{t("product_complete_the_look")}</p>
      <div className={css.shopTheLookCtaThumbnails}>
        {shopTheLookItems
          .filter((item) => getPackshotFromImages(item.images).image)
          .slice(0, isMoreThanMaxThumbnails ? MAX_THUMBNAILS - 1 : MAX_THUMBNAILS)
          .map((item, index) => {
            const { image, isPackshot } = getPackshotFromImages(item.images)

            if (!image) {
              return null
            }

            return (
              <Image
                asPlaceholder
                withBackground={isPackshot}
                imageClassName={isPackshot ? css.objectFitContain : undefined}
                className={css.shopTheLookCtaThumbnail}
                key={`shopTheLook-Thumbnail-${index}`}
                {...image}
                ratio="1_1"
              />
            )
          })}
        {isMoreThanMaxThumbnails && <div className={css.shopTheLookCtaThumbnailCta}>+</div>}
      </div>
    </button>
  )
}
